import { useCallback, useEffect, useState } from 'react';
import * as Sentry from '@sentry/react';
import { resetPassword, type ResetPasswordOutput } from 'aws-amplify/auth';
import ErrorText from '~/components/SignUp/Error/ErrorText';
import InnerLayout from '~/components/SignUp/Layout/InnerLayout';
import { Input } from '~/components/atomic';
import { fetchCheckEmail } from '~/api';
import { useDebounce } from '~/hooks';
import { isGmail, validateEmail } from '~/utils';
import * as S from './CheckEmail.styled';

const CHECK_EMAIL_TIME = 500;
const EMAIL_VALIDATE_MSG = 'Please enter a valid email.';
const GOOGLE_VALIDATE_MSG =
  'Please use Social login for Gmail email addresses.';
const USER_ACCOUNT_MSG = 'Account does not exist. Please sign up.';

interface CheckEmailProps {
  email: string;
  setEmail: (email: string) => void;
  onNextStep: () => void;
}

export default function CheckEmail({
  email,
  setEmail,
  onNextStep,
}: CheckEmailProps) {
  const [disabled, setDisabled] = useState(true);
  const [error, setError] = useState('');

  const debouncedEmail = useDebounce(email, CHECK_EMAIL_TIME);

  const checkEmail = useCallback(async () => {
    if (!validateEmail(debouncedEmail)) {
      setError(EMAIL_VALIDATE_MSG);
      setDisabled(true);
      return;
    }

    if (isGmail(debouncedEmail)) {
      setError(GOOGLE_VALIDATE_MSG);
      setDisabled(true);
      return;
    }

    try {
      const { data } = await fetchCheckEmail({ email: debouncedEmail });
      //사용 중이면 0, 아니라면 1
      if (data.used) {
        // 사용중
        setError('');
        setDisabled(false);
      } else {
        setError(USER_ACCOUNT_MSG);
        setDisabled(true);
      }
    } catch (error) {
      console.error(error);
    }
  }, [debouncedEmail]);

  const handleOnClick = async () => {
    try {
      const output = await resetPassword({ username: email });
      const result = handleResetPasswordNextSteps(output);

      if (result === 'CONFIRM_RESET_PASSWORD_WITH_CODE') {
        onNextStep();
      }
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    if (debouncedEmail === '') {
      setError('');
      setDisabled(true);
      return;
    }

    checkEmail();
  }, [debouncedEmail, checkEmail]);

  const handleResetPasswordNextSteps = (output: ResetPasswordOutput) => {
    const { nextStep } = output;
    switch (nextStep.resetPasswordStep) {
      case 'CONFIRM_RESET_PASSWORD_WITH_CODE':
        {
          const codeDeliveryDetails = nextStep.codeDeliveryDetails;
          console.log(
            `Confirmation code was sent to ${codeDeliveryDetails.deliveryMedium}`,
          );
          return 'CONFIRM_RESET_PASSWORD_WITH_CODE';
        }
        break;
      case 'DONE':
        return 'DONE';
        break;
    }
  };

  const buttonProps = {
    title: 'Send Verification code',
    disabled,
    onClick: handleOnClick,
  };

  return (
    <InnerLayout buttonProps={buttonProps}>
      <S.Container>
        <div>
          <S.SubTitle>
            Enter the email address you use. <br />
            We’ll send you a password reset link.
          </S.SubTitle>
        </div>
        <div>
          <Input
            id='ResetPasswordCheckEmail'
            placeholder='Email'
            text={email}
            setText={setEmail}
            deleteAllTextOpt
          />
          {error && <ErrorText error={error} />}
        </div>
      </S.Container>
    </InnerLayout>
  );
}
